import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { DOCUMENT } from '../../constants/documentTheme';
import { developerPath, mainAppOrigin, publicApiBase } from '../../utils/developerHost';
import {
  extractTripPreviewFromCard,
  isTripWidgetCta,
} from '../../utils/developerSandboxTripPreview';
import SandboxClientTripPreview from '../../components/SandboxClientTripPreview';
import SandboxWidgetStyleDemo from '../../components/SandboxWidgetStyleDemo';

const sectionSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  boxShadow: 'none',
  p: { xs: 2.5, md: 3 },
  mb: 3,
};

const codeSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12.5,
  lineHeight: 1.55,
  bgcolor: DOCUMENT.ivory,
  border: `1px solid ${DOCUMENT.rule}`,
  p: 2,
  overflowX: 'auto',
  whiteSpace: 'pre',
  color: DOCUMENT.ink,
  m: 0,
};

function CopyCode({ code, copyLabel, copiedLabel }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box sx={{ position: 'relative', mt: 1.5 }}>
      <Box component="pre" sx={codeSx}>{code}</Box>
      <Button
        size="small"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {
            // ignore
          }
        }}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          minWidth: 0,
          px: 1.25,
          fontSize: 12,
          textTransform: 'none',
          color: DOCUMENT.ink2,
          bgcolor: DOCUMENT.surface,
          border: `1px solid ${DOCUMENT.rule}`,
          borderRadius: 0,
        }}
      >
        {copied ? copiedLabel : copyLabel}
      </Button>
    </Box>
  );
}

function ExamplePanel({
  title,
  method,
  path,
  request,
  response,
  extra,
  onReplay,
  replaying,
  replayed,
  t,
}) {
  const copyLabel = t('developerPortal.docs.copy');
  const copiedLabel = t('developerPortal.docs.copied');
  return (
    <Paper elevation={0} sx={sectionSx}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22 }}>
          {title}
        </Typography>
        <Chip label={method} size="small" sx={{ borderRadius: 0, bgcolor: DOCUMENT.ivory }} />
        <Chip label={path} size="small" variant="outlined" sx={{ borderRadius: 0, fontFamily: 'monospace' }} />
      </Box>

      <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, mb: 2, maxWidth: '68ch' }}>
        {t('developerPortal.sandbox.exampleNote')}
      </Typography>

      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.5 }}>
        {t('developerPortal.apiRef.requestBody')}
      </Typography>
      <CopyCode
        code={JSON.stringify(request, null, 2)}
        copyLabel={copyLabel}
        copiedLabel={copiedLabel}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mt: 2.5, mb: 1 }}>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>
          {t('developerPortal.apiRef.response')}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={replaying}
          onClick={onReplay}
          sx={{
            borderRadius: 0,
            textTransform: 'none',
            borderColor: DOCUMENT.ruleHard,
            color: DOCUMENT.ink,
          }}
        >
          {replaying ? t('developerPortal.sandbox.replaying') : t('developerPortal.sandbox.replay')}
        </Button>
        {replayed ? (
          <Chip
            label={t('developerPortal.sandbox.simulatedBadge')}
            size="small"
            color="success"
            variant="outlined"
            sx={{ borderRadius: 0 }}
          />
        ) : null}
      </Box>
      <CopyCode
        code={JSON.stringify(response, null, 2)}
        copyLabel={copyLabel}
        copiedLabel={copiedLabel}
      />

      {extra}
    </Paper>
  );
}

const DeveloperSandbox = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sandbox, setSandbox] = useState(null);
  const [tripReplay, setTripReplay] = useState(null);
  const [pkgReplay, setPkgReplay] = useState(null);
  const [replayingTrip, setReplayingTrip] = useState(false);
  const [replayingPkg, setReplayingPkg] = useState(false);
  const [widgetsReady, setWidgetsReady] = useState(false);
  const [tripPreview, setTripPreview] = useState(null);
  const [widgetStyle, setWidgetStyle] = useState({ theme: 'light', style: {} });

  const apiBase = publicApiBase();
  const widgetHost = mainAppOrigin();
  const widgetSrc = `${widgetHost}/widget.js`;
  const linkSiteBase = 'https://dejavunomad.com';

  const handleStyleChange = useCallback((next) => {
    setWidgetStyle(next || { theme: 'light', style: {} });
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/developer/sandbox');
      setSandbox(res.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || t('developerPortal.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const catalogToken = sandbox?.catalog_token;
  const useLiveCatalog = Boolean(catalogToken);

  useEffect(() => {
    if (!useLiveCatalog || tab !== 2) {
      setWidgetsReady(false);
      return undefined;
    }

    const packagesHost = document.getElementById('dvn-sandbox-packages');
    const tripsHost = document.getElementById('dvn-sandbox-trips');
    if (!packagesHost || !tripsHost) return undefined;

    packagesHost.innerHTML = '';
    tripsHost.innerHTML = '';

    const pkg = document.createElement('div');
    pkg.setAttribute('data-dvn-catalog', catalogToken);
    packagesHost.appendChild(pkg);

    const trips = document.createElement('div');
    trips.setAttribute('data-dvn-trips', catalogToken);
    tripsHost.appendChild(trips);

    window.DVN_SITE_BASE = linkSiteBase;
    window.DVN_API_BASE = apiBase;

    document.querySelectorAll('script[data-dvn-sandbox-widget]').forEach((el) => el.remove());
    const script = document.createElement('script');
    script.src = widgetSrc;
    script.async = true;
    script.dataset.dvnSandboxWidget = '1';
    script.setAttribute('data-api', apiBase);
    script.setAttribute('data-site', linkSiteBase);
    script.onload = () => {
      if (window.DVNWidgets?.refresh) window.DVNWidgets.refresh();
      setWidgetsReady(true);
    };
    script.onerror = () => setWidgetsReady(false);
    document.body.appendChild(script);

    const onTripCtaClick = (event) => {
      const anchor = event.target?.closest?.('a.dvn-cta');
      if (!isTripWidgetCta(anchor, tripsHost)) return;
      event.preventDefault();
      event.stopPropagation();
      const preview = extractTripPreviewFromCard(anchor);
      if (preview) setTripPreview(preview);
    };
    tripsHost.addEventListener('click', onTripCtaClick, true);

    return () => {
      tripsHost.removeEventListener('click', onTripCtaClick, true);
      document.querySelectorAll('script[data-dvn-sandbox-widget]').forEach((el) => el.remove());
    };
  }, [useLiveCatalog, catalogToken, tab, apiBase, linkSiteBase, widgetSrc]);

  useEffect(() => {
    if (!useLiveCatalog || tab !== 2 || !widgetsReady) return undefined;

    const apply = (hostId) => {
      const host = document.getElementById(hostId);
      if (!host) return;
      const mount = host.querySelector('.dvn-widget') || host.firstElementChild || host;
      if (widgetStyle.theme === 'dark') mount.setAttribute('data-dvn-theme', 'dark');
      else mount.removeAttribute('data-dvn-theme');
      Object.entries(widgetStyle.style || {}).forEach(([key, value]) => {
        mount.style.setProperty(key, value);
      });
    };

    apply('dvn-sandbox-packages');
    apply('dvn-sandbox-trips');
    return undefined;
  }, [useLiveCatalog, tab, widgetsReady, widgetStyle]);

  const replay = async (kind) => {
    const isTrip = kind === 'trip';
    if (isTrip) setReplayingTrip(true);
    else setReplayingPkg(true);
    try {
      const res = await api.post(`/developer/sandbox/simulate/${kind}`);
      if (isTrip) setTripReplay(res.data);
      else setPkgReplay(res.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || t('developerPortal.errors.loadFailed'));
    } finally {
      if (isTrip) setReplayingTrip(false);
      else setReplayingPkg(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  const tripEx = sandbox?.examples?.create_trip;
  const pkgEx = sandbox?.examples?.create_package;
  const embedSnippet = `<!-- Use your own catalog token from the partner Packages page -->
<script src="${widgetSrc}" data-api="${apiBase}" data-site="${linkSiteBase}"></script>

<div data-dvn-catalog="YOUR_CATALOG_TOKEN"></div>
<div data-dvn-trips="YOUR_CATALOG_TOKEN"></div>`;

  return (
    <Box>
      <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 34 }, mb: 0.75 }}>
        {t('developerPortal.sandbox.title')}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink2, mb: 2, maxWidth: '70ch' }}>
        {t('developerPortal.sandbox.subtitle')}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Chip label={t('developerPortal.sandbox.badgeViewOnly')} color="success" variant="outlined" sx={{ borderRadius: 0 }} />
        <Chip label={t('developerPortal.sandbox.badgeNoKey')} sx={{ borderRadius: 0 }} />
        <Chip label={t('developerPortal.sandbox.badgeFixtures')} variant="outlined" sx={{ borderRadius: 0 }} />
        {useLiveCatalog && sandbox?.company_name ? (
          <Chip label={sandbox.company_name} variant="outlined" sx={{ borderRadius: 0 }} />
        ) : null}
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Alert severity="info" sx={{ mb: 3, borderRadius: 0 }}>
        {t('developerPortal.sandbox.info')}
        {' '}
        <Button
          component={RouterLink}
          to={developerPath('keys')}
          size="small"
          sx={{ textTransform: 'none', ml: 0.5 }}
        >
          {t('developerPortal.sandbox.useYourKeys')}
        </Button>
      </Alert>

      <Tabs
        value={tab}
        onChange={(_e, next) => setTab(next)}
        sx={{
          mb: 3,
          minHeight: 40,
          borderBottom: `1px solid ${DOCUMENT.rule}`,
          '& .MuiTab-root': { textTransform: 'none', minHeight: 40, borderRadius: 0 },
          '& .Mui-selected': { color: DOCUMENT.oxblood },
          '& .MuiTabs-indicator': { bgcolor: DOCUMENT.oxblood },
        }}
      >
        <Tab label={t('developerPortal.sandbox.tabTrip')} />
        <Tab label={t('developerPortal.sandbox.tabPackage')} />
        <Tab label={t('developerPortal.sandbox.tabWidgets')} />
      </Tabs>

      {tab === 0 && tripEx ? (
        <ExamplePanel
          title={t('developerPortal.sandbox.createTripTitle')}
          method={tripEx.method}
          path={tripEx.path}
          request={tripEx.request}
          response={tripReplay?.response || tripEx.response}
          replaying={replayingTrip}
          replayed={Boolean(tripReplay)}
          onReplay={() => replay('trip')}
          t={t}
          extra={tripEx.itinerary_follow_up ? (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.5 }}>
                {t('developerPortal.sandbox.itineraryFollowUp')}
              </Typography>
              <Typography sx={{ color: DOCUMENT.ink2, fontSize: 13.5, mb: 1 }}>
                {tripEx.itinerary_follow_up.method} {tripEx.itinerary_follow_up.path}
              </Typography>
              <CopyCode
                code={JSON.stringify(tripEx.itinerary_follow_up.request, null, 2)}
                copyLabel={t('developerPortal.docs.copy')}
                copiedLabel={t('developerPortal.docs.copied')}
              />
            </Box>
          ) : null}
        />
      ) : null}

      {tab === 1 && pkgEx ? (
        <ExamplePanel
          title={t('developerPortal.sandbox.createPackageTitle')}
          method={pkgEx.method}
          path={pkgEx.path}
          request={pkgEx.request}
          response={pkgReplay?.response || pkgEx.response}
          replaying={replayingPkg}
          replayed={Boolean(pkgReplay)}
          onReplay={() => replay('package')}
          t={t}
          extra={pkgEx.publish ? (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.5 }}>
                {t('developerPortal.sandbox.publishFollowUp')}
              </Typography>
              <Typography sx={{ color: DOCUMENT.ink2, fontSize: 13.5, mb: 1 }}>
                {pkgEx.publish.method} {pkgEx.publish.path}
              </Typography>
              <CopyCode
                code={JSON.stringify(pkgEx.publish.response, null, 2)}
                copyLabel={t('developerPortal.docs.copy')}
                copiedLabel={t('developerPortal.docs.copied')}
              />
            </Box>
          ) : null}
        />
      ) : null}

      {tab === 2 ? (
        <Box>
          <Paper elevation={0} sx={sectionSx}>
            <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
              {t('developerPortal.sandbox.widgetsTitle')}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, mb: 2, maxWidth: '68ch' }}>
              {t('developerPortal.sandbox.widgetsBody')}
            </Typography>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.5 }}>
              {t('developerPortal.sandbox.embedCode')}
            </Typography>
            <CopyCode
              code={embedSnippet}
              copyLabel={t('developerPortal.docs.copy')}
              copiedLabel={t('developerPortal.docs.copied')}
            />
          </Paper>

          <SandboxWidgetStyleDemo
            packageFixture={sandbox?.fixtures?.widgets?.packages?.[0] || null}
            tripFixture={sandbox?.fixtures?.widgets?.trips?.[0] || null}
            onOpenTrip={setTripPreview}
            onStyleChange={handleStyleChange}
          />

          <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
            {t('developerPortal.sandbox.samplePackageNote')}
            {' '}
            {t('developerPortal.sandbox.tripPreviewHint')}
          </Alert>

          {useLiveCatalog ? (
            <>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
                {t('developerPortal.sandbox.liveCatalogNote')}
              </Alert>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 1.5 }}>
                {t('developerPortal.sandbox.packagesLive')}
              </Typography>
              <Box
                id="dvn-sandbox-packages"
                className="dvn-widget"
                data-dvn-theme={widgetStyle.theme === 'dark' ? 'dark' : undefined}
                style={widgetStyle.style}
                sx={{ mb: 4, minHeight: 120 }}
              />

              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 1 }}>
                {t('developerPortal.sandbox.tripsLive')}
              </Typography>
              <Box
                id="dvn-sandbox-trips"
                className="dvn-widget"
                data-dvn-theme={widgetStyle.theme === 'dark' ? 'dark' : undefined}
                style={widgetStyle.style}
                sx={{ mb: 2, minHeight: 120 }}
              />
              <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 2 }}>
                {widgetsReady
                  ? t('developerPortal.sandbox.widgetsLoaded')
                  : t('developerPortal.sandbox.widgetsLoading')}
              </Typography>
            </>
          ) : null}

          <Button
            component={RouterLink}
            to={developerPath('widgets')}
            sx={{ mt: 1, textTransform: 'none', color: DOCUMENT.oxblood }}
          >
            {t('developerPortal.sandbox.widgetsDocsCta')}
          </Button>
        </Box>
      ) : null}

      <SandboxClientTripPreview
        open={Boolean(tripPreview?.tripRef)}
        tripRef={tripPreview?.tripRef || null}
        fallbackHref={tripPreview?.href || null}
        onClose={() => setTripPreview(null)}
      />
    </Box>
  );
};

export default DeveloperSandbox;
